import { Build, Step } from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";
import { queryOptions, UseQueryOptions } from "@tanstack/react-query";
import { FilterByOption } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { setStepQueryData } from "~/components/build/Show/lib/queries/step";

/**
 * All steps query
 */
export const allStepsQueryKey = (build: Build) => {
  return ["build", build.id, "steps", "all"];
};

export const allStepsQueryOptions = (
  build: Build,
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: allStepsQueryKey(build),
    queryFn: ({ client: queryClient }) =>
      fetchBuildSteps({ buildDataBasePath: build.buildDataBasePath }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Top-level steps query
 */

export const topLevelStepsQueryKey = (build: Build) => {
  return ["build", build.id, "steps", "depth:top"];
};

export const topLevelStepsQueryOptions = (
  build: Build,
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: topLevelStepsQueryKey(build),
    queryFn: ({ client: queryClient }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { depth: "top" },
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Children steps query (for group steps)
 */
export const childrenStepsQueryKey = (build: Build, stepUuid: Step["uuid"]) => {
  return ["build", build.id, "steps", stepUuid, "children"];
};

export const childrenStepsQueryOptions = (
  build: Build,
  stepUuid: Step["uuid"],
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: childrenStepsQueryKey(build, stepUuid),
    queryFn: ({ client: queryClient }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { groupUuid: stepUuid },
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Steps by state
 */
export const stepsByStateQueryKey = (build: Build, state: FilterByOption) => {
  return ["build", build.id, "steps", `filter:${state}`];
};

export const stepsByStateQueryOptions = (
  build: Build,
  state: FilterByOption,
  queryOptionsArgs: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: stepsByStateQueryKey(build, state),
    queryFn: ({ client: queryClient }) =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { state, excludeGroupSteps: true },
      }).then((steps) => {
        steps.forEach((step) => setStepQueryData(build, step, queryClient));
        return steps;
      }),
    ...queryOptionsArgs,
  });
};

/**
 * Fetches the steps for a build optionally filtered by state.
 */

type FetchBuildStepsParams = {
  buildDataBasePath: Build["buildDataBasePath"];
  filters?: {
    state?: FilterByOption;
    excludeGroupSteps?: boolean;
    depth?: "top" | "all";
    groupUuid?: Step["groupUuid"];
  };
};

async function fetchBuildSteps({
  buildDataBasePath,
  filters,
}: FetchBuildStepsParams): Promise<Step[]> {
  const url = new URL(`${buildDataBasePath}/steps`, window.location.origin);

  // Note: The order of the parameters matters since rails will automatically order search params alphabetically.
  // This means in order to prefetch this query we need to ensure that the parameters are in the same order.

  if (filters && filters.depth !== undefined) {
    url.searchParams.append("depth", String(filters.depth));
  }

  if (filters && filters.excludeGroupSteps !== undefined) {
    url.searchParams.append("exclude_group_steps", String(filters.excludeGroupSteps));
  }

  if (filters && filters.groupUuid !== undefined) {
    url.searchParams.append("group_uuid", filters.groupUuid);
  }

  if (filters?.state) {
    url.searchParams.append("state", filters.state);
  }

  return fetcher(url);
}
